import { type KeyboardEvent } from 'react'
import { observer } from 'mobx-react-lite'
import { type Observable } from 'rxjs'
import type { QuestionAnswerEditorViewModelBase } from '@/features/jobs/questions/QuestionAnswerEditorViewModelBase'
import type { Question } from '@/features/jobs/questions/questions'
import { BoolQuestionAnswerEditorViewModel } from '@/features/jobs/questions/bool/BoolQuestionAnswerEditorViewModel'
import { BoolQuestionAnswerEditorContainer } from '@/features/jobs/questions/bool/components/BoolQuestionAnswerEditor/BoolQuestionAnswerEditorContainer'
import { SelectQuestionAnswerEditorViewModel } from '@/features/jobs/questions/select/SelectQuestionAnswerEditorViewModel'
import { SelectQuestionAnswerEditorContainer } from '@/features/jobs/questions/select/components/SelectQuestionAnswerEditor/SelectQuestionAnswerEditorContainer'
import { DateTimeQuestionAnswerEditorViewModel } from '@/features/jobs/questions/date-time/DateTimeQuestionAnswerEditorViewModel'
import { DateTimeQuestionAnswerEditorContainer } from '@/features/jobs/questions/date-time/components/DateTimeQuestionAnswerEditor/DateTimeQuestionAnswerEditorContainer'
import { NumericQuestionAnswerEditorViewModel } from '@/features/jobs/questions/numeric/NumericQuestionAnswerEditorViewModel'
import { NumericQuestionAnswerEditorContainer } from '@/features/jobs/questions/numeric/components/NumericQuestionAnswerEditor/NumericQuestionAnswerEditorContainer'
import { TextQuestionAnswerEditorViewModel } from '@/features/jobs/questions/text/TextQuestionAnswerEditorViewModel'
import { TextQuestionAnswerEditorContainer } from '@/features/jobs/questions/text/components/TextQuestionAnswerEditor/TextQuestionAnswerEditorContainer'
import { DocumentsQuestionAnswerEditorViewModel } from '@/features/jobs/questions/documents/DocumentsQuestionAnswerEditorViewModel'
import { DocumentsQuestionAnswerEditorContainer } from '@/features/jobs/questions/documents/components/DocumentsQuestionAnswerEditor/DocumentsQuestionAnswerEditorContainer'
import { BreakerQuestionAnswerEditorViewModel } from '@/features/jobs/questions/breaker/BreakerQuestionAnswerEditorViewModel'
import { UnknownQuestionAnswerEditorViewModel } from '@/features/jobs/questions/unknown/UnknownQuestionAnswerEditorViewModel'
import { UnknownQuestionAnswerEditor } from '@/features/jobs/questions/unknown/components/UnknownQuestionAnswerEditor'

export interface AnswerEditorRendererContainerProps {
  questionId: string
  editorVm: QuestionAnswerEditorViewModelBase<Question>
  isDisabled: boolean
  focus$?: Observable<unknown>
  onFocus?: () => unknown
  onEnterDown?: (e: KeyboardEvent) => unknown
  isNotValid?: boolean
}

/**
 * Renders the appropriate answer editor container.
 */
export const AnswerEditorRendererContainer = observer(
  function AnswerEditorRendererContainer({
    editorVm,
    ...rest
  }: AnswerEditorRendererContainerProps) {
    switch (true) {
      case editorVm instanceof BoolQuestionAnswerEditorViewModel: {
        return (
          <BoolQuestionAnswerEditorContainer editorVm={editorVm} {...rest} />
        )
      }
      case editorVm instanceof SelectQuestionAnswerEditorViewModel: {
        return (
          <SelectQuestionAnswerEditorContainer editorVm={editorVm} {...rest} />
        )
      }
      case editorVm instanceof DateTimeQuestionAnswerEditorViewModel: {
        return (
          <DateTimeQuestionAnswerEditorContainer
            editorVm={editorVm}
            {...rest}
          />
        )
      }
      case editorVm instanceof NumericQuestionAnswerEditorViewModel: {
        return (
          <NumericQuestionAnswerEditorContainer editorVm={editorVm} {...rest} />
        )
      }
      case editorVm instanceof TextQuestionAnswerEditorViewModel: {
        return (
          <TextQuestionAnswerEditorContainer editorVm={editorVm} {...rest} />
        )
      }
      case editorVm instanceof DocumentsQuestionAnswerEditorViewModel: {
        return (
          <DocumentsQuestionAnswerEditorContainer
            editorVm={editorVm}
            isDisabled={rest.isDisabled}
            focus$={rest.focus$}
            onFocus={rest.onFocus}
          />
        )
      }
      case editorVm instanceof BreakerQuestionAnswerEditorViewModel: {
        return null
      }
      case editorVm instanceof UnknownQuestionAnswerEditorViewModel: {
        return <UnknownQuestionAnswerEditor type={editorVm.questionType} />
      }
      default:
        return null
    }
  },
)
